<template>
    <div>
        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>
            <vue-headful :title="incidentName+ ' [Change Incident Type] :: MeritEHS'"/>

            <h4 class="py-3 mb-4">
                <ol class="breadcrumb font-weight-bold m-0">
                    <li class="breadcrumb-item font-weight-light">
                        <router-link :to="`/incidents`">
                            <i class="fa fa-stethoscope"></i> Incidents
                        </router-link>
                    </li>
                    <li class="breadcrumb-item font-weight-light">
                        <router-link :to="`/incidents/view/${incident.IncidentId}`">
                            <incident-name :incident="incident" />
                        </router-link>
                    </li>
                    <li class="breadcrumb-item active">
                        Change Incident Type
                    </li>
                </ol>
            </h4>

            <div class="row mb-3">
                <div class="col-lg-6 col-md-7">
                    <div class="card mb-3">
                        <div class="card-body">
                            <div class="form-horizontal">
                                <div class="form-group row">
                                    <label class="col-md-4 text-muted">Existing Incident Type:</label>
                                    <div class="col-md-7">
                                        {{ incident.IncidentTypeName }}
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-md-4 text-muted">New Incident Type:</label>
                                    <div class="col-md-7">
                                        <b-form-select v-model="payload.incident_type_id" :options="typeOptions"></b-form-select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-6 col-md-5">
                    <div class="alert alert-info">
                        <h5>Migration Help:</h5>
                        <p v-if="show_help">To preserve custom field data, MeritEHS uses the below migration process. For each field, you have the following options:
                            <ol>
                                <li><strong>Map to a Field of the Same Type:</strong> Pick a field of the same type to move the value to this field.</li>
                                <li><strong>Radio/Dropdown/Checkbox Fields:</strong> These require you to pick a new value via the New Value dropdown that shows up.</li>
                                <li><strong>Read Only:</strong> This will keep the existing data and it becomes Read-Only when viewing the incident.</li>
                                <li><strong>Delete Field:</strong> This will keep delete the existing data and the field will be removed.</li>
                            </ol>
                        </p>
                    </div>
                </div>
            </div>

            <template v-if="payload.incident_type_id">
                <div class="card mb-3" v-for="(section, i) in sections">
                    <div class="card-header">
                        <span class="font-weight-bold">{{ getSectionName(section) }}</span> - Map Custom Fields
                    </div>
                    <div class="card-body">
                        <div class="row mb-2" v-for="(field, j) in section.fields">
                            <div class="col-lg-4">
                                {{ field.label }}:

                                <div v-if="field.type == 'Textarea'" v-html="field.value"></div>
                                <span v-else-if="field.type == 'Jobsite'" class="font-weight-bold">{{ field.value.JobsiteNumber }} - {{ field.value.JobsiteName }}</span>
                                <span v-else-if="field.type == 'Employee'" class="font-weight-bold">{{ field.value.EmployeeNumber }} - {{ field.value.FirstName }} {{ field.value.LastName }}</span>
                                <span v-else-if="field.type == 'Equipment'" class="font-weight-bold">
                                    {{ field.value.equipment_type_name }} - {{ field.value.serial_number }} <template v-if="field.value.model_number">({{ field.value.model_number }})</template>
                                </span>
                                <span v-else-if="field.type == 'User'" class="font-weight-bold">{{ field.value.FirstName }} - {{ field.value.LastName }}</span>
                                <span v-else-if="field.type == 'File'" class="font-weight-bold">{{ field.value.file_name }}</span>
                                <template v-else-if="Array.isArray(field.value)">
                                    <span class="font-weight-bold" v-for="(val, i) in field.value">
                                        <template v-if="i > 0">,</template> {{ val }}
                                    </span>
                                </template>
                                <span class="font-weight-bold" v-else>
                                    {{ field.value }}
                                </span>
                            </div>
                            <div class="col-lg-4">
                                <b-form-select v-model="field.new_field_id" size="sm" :options="fieldOptions(field.type)" v-on:change="updateField(field.cf_section_id, field.id)" :class="{'is-invalid': errors['fields.'+getFieldIndex(field.id)+'.new_field_id']}"></b-form-select>

                                <div v-if="errors['fields.'+getFieldIndex(field.id)+'.new_field_id']" class="text-danger">
                                    <div v-for="err in errors['fields.'+getFieldIndex(field.id)+'.new_field_id']">
                                        {{ err }}
                                    </div>
                                </div>
                            </div>
                            <div class="col-lg-4" v-if="field.new_field_id && field.new_field_id != 'ready_only' && field.new_field_id != 'delete_field'">
                                <b-form-select v-model="field.new_value" size="sm" :options="newFieldOptions(field.new_field_id)" v-if="field.type == 'Radio' || field.type == 'Dropdown'" :class="{'is-invalid': errors['fields.'+getFieldIndex(field.id)+'.new_value']}"></b-form-select>
                                <b-form-checkbox-group v-model="field.new_value" :options="newFieldOptions(field.new_field_id)" v-if="field.type == 'Checkbox'" plain></b-form-checkbox-group>

                                <template v-if="errors['fields.'+getFieldIndex(field.id)+'.new_value']">
                                    <div class="text-danger small" v-for="err in errors['fields.'+getFieldIndex(field.id)+'.new_value']">{{ err }}</div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <button type="button" class="btn btn-success" v-on:click="saveIncident(this)" :disabled="saving" v-if="payload.incident_type_id">
                <span v-if="saving">Changing</span>
                <span v-else>Change</span>
                Incident Type
            </button>
        </div>
    </div>
</template>

<script>
    import moment from 'moment-timezone';

    export default {
        data: function() {
            return {
                loading: true,
                saving: false,
                show_help: true,
                field_index: 0,
                IncidentId: this.$route.params.id,
                incident: {},
                currentType: {},
                otherTypes: [],
                errors: {},
                sections: {},
                payload: {
                    incident_type_id: null,
                    fields: {},
                }
            }
        },

        computed: {
            typeOptions(){
                //Setup
                var options = [{value: null, text: '-- Select an Incident Type --'}];

                //Loop and Build
                for(var i = 0; i < this.otherTypes.length; i++)
                {
                    options.push({value: this.otherTypes[i].IncidentTypeId, text: this.otherTypes[i].TypeName});
                }

                //Send it
                return options;
            },

            incidentDateFormatted(){
                //Format it
                return moment(this.incident.IncidentDate, 'YYYY-MM-DD').format('MM/DD/YYYY');
            },

            incidentName(){
                //Setup
                var name = '';

                //Add the TypeName
                name += (this.incident.IncidentTypeName ? this.incident.IncidentTypeName : 'Incident') + '-';

                //Add the Date
                name += this.incidentDateFormatted + '-';

                //Add the Jobsite
                name += this.incident.JobsiteId ? this.incident.JobsiteName : this.incident.other_location;

                //Send it
                return name;
            },

            fields(){
                //Setup and send ONLY the fields
                var fields = [];

                //Build it
                for(var t = 0; t < this.sections.length; t++)
                {
                    //Loop and Build
                    for(var j = 0; j < this.sections[t].fields.length; j++)
                    {
                        //Push it
                        fields.push(this.sections[t].fields[j]);
                    }
                }

                //Send it
                return fields;
            },
        },

        watch: {

        },

        methods: {

            getIncident() {
                //Get the data
                this.$http
                    .get('incident/change-type/' + this.IncidentId)
                    .then(response => {
                        //Set all the data
                        this.incident = response.data.incident;
                        this.currentType = response.data.type;
                        this.otherTypes = response.data.otherTypes;

                        //Setup the fields
                        var sections = [];
                        var fields = {};
                        var keys = Object.keys(this.currentType.sections);

                        //Build it
                        for(var t = 0; t < keys.length; t++)
                        {
                            sections.push({
                                id: this.currentType.sections[keys[t]].id,
                                base_section: this.currentType.sections[keys[t]].base_section,
                                section_name: this.currentType.sections[keys[t]].section_name,
                                fields: [],
                            });

                            //Setup
                            var fieldKeys = Object.keys(this.currentType.sections[keys[t]].fields);

                            //Loop and Build
                            for(var j = 0; j < fieldKeys.length; j++)
                            {
                                //Skip Headers, Paragraphs, Etc.
                                if(this.currentType.sections[keys[t]].fields[fieldKeys[j]].type == 'Heading' || this.currentType.sections[keys[t]].fields[fieldKeys[j]].type == 'Paragraph')
                                {
                                    continue;
                                }

                                //Setup
                                var newValue = this.currentType.sections[keys[t]].fields[fieldKeys[j]].value;
                                var optionTypes = ['Radio', 'Dropdown', 'Checkbox'];

                                //Set to NULL
                                if(optionTypes.indexOf(this.currentType.sections[keys[t]].fields[fieldKeys[j]].type) !== -1)
                                {
                                    newValue = null;
                                }

                                //Append
                                sections[t].fields[j] = {
                                    new_field_id: null,
                                    new_value: newValue,
                                    cf_section_id: this.currentType.sections[keys[t]].id,
                                    cf_value_id: this.currentType.sections[keys[t]].fields[fieldKeys[j]].cf_value_id,
                                    id: this.currentType.sections[keys[t]].fields[fieldKeys[j]].id,
                                    label: this.currentType.sections[keys[t]].fields[fieldKeys[j]].label,
                                    value: this.currentType.sections[keys[t]].fields[fieldKeys[j]].value,
                                    type: this.currentType.sections[keys[t]].fields[fieldKeys[j]].type,
                                };
                            }
                        }

                        //Set it
                        this.sections = sections;
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },

            saveIncident: function() {
                //SHow loading
                this.saving = true;

                //Reset the errors
                this.errors = {};

                //Do extra stuff?

                //Send it
                this.$http
                .post('incident/change-type/' + this.IncidentId, {incident_type_id: this.payload.incident_type_id, fields: this.fields})
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('Incident Type Changed');

                    //Return to main list?
                    this.$router.push('/incidents/edit/' + this.IncidentId);
                })
                .catch(error => {
                    //Check for error
                    if(error.response.status === 422)
                    {
                        //Display validation errors
                        this.errors = error.response.data;

                        //Loop and build the errors for each field
                        // for(var i = 0; i < fields.length; i++)
                        // {
                        //     //Check for an error
                        //     if(this.errors['fields.'+i+'.new_value'])
                        //     {
                        //         //Set the error
                        //         this.errors['fields.'+this.fields[i].id+'.new_value'] = this.errors['fields.'+i+'.new_value'];
                        //     }
                        // }
                    }
                })
                .then(() => {
                    //SHow loading
                    this.saving = false;
                });
            },

            getSectionName(section){
                //IF base section is null, send name
                if(!section.base_section)
                {
                    return section.section_name;
                }

                //Prettify the name
                var nameParts = section.base_section.split('_');
                var returnName = '';

                //Loop and Capitalize
                for (var i = 0; i < nameParts.length; i++)
                {
                    returnName += (i > 0 ? ' ' : '') + nameParts[i].charAt(0).toUpperCase() + nameParts[i].slice(1);
                }

                //Send it
                return returnName;
            },

            fieldOptions(type){
                //IF a new incident has not been selected, show placeholder
                if(!this.payload.incident_type_id)
                {
                    return [{value: null, text: '-- Select a New Incident Type to Load Field Options --'}];
                }

                //Setup
                var options = [
                    {value: null, text: '-- Select a New Field, Select to Leave Blank to Store as Read-Only, or Select Delete Field --'}
                ];

                //Loop and Build
                for(var i = 0; i < this.otherTypes.length; i++)
                {
                    //Compare ID
                    if(this.otherTypes[i].IncidentTypeId != this.payload.incident_type_id)
                    {
                        continue;
                    }

                    for(var t = 0; t < this.otherTypes[i].sections.length; t++)
                    {
                        //Loop and Build
                        for(var j = 0; j < this.otherTypes[i].sections[t].fields.length; j++)
                        {
                            //Skip Headers, Paragraphs, Etc.
                            if(this.otherTypes[i].sections[t].fields[j].type !== type)
                            {
                                continue;
                            }

                            //Append
                            options.push({value: this.otherTypes[i].sections[t].fields[j].id, text: this.otherTypes[i].sections[t].fields[j].label});
                        }
                    }
                }

                //Add Fixed Options
                options.push({value: 'read_only', text: '-- Store as Ready-Only --'});
                options.push({value: 'delete_field', text: '-- Delete Field --'});

                //Send it
                return options;
            },

            newFieldOptions(new_field_id){
                //Loop and build
                var options = [{value: null, text: '-- Select a New Value --'}];

                //Loop and Build
                for(var i = 0; i < this.otherTypes.length; i++)
                {
                    for(var t = 0; t < this.otherTypes[i].sections.length; t++)
                    {
                        //Loop and Build
                        for(var j = 0; j < this.otherTypes[i].sections[t].fields.length; j++)
                        {
                            //Check ID
                            if(new_field_id == this.otherTypes[i].sections[t].fields[j].id)
                            {
                                //Set the options
                                //options = this.otherTypes[i].sections[t].fields[j].options;
                                var keys = Object.keys(this.otherTypes[i].sections[t].fields[j].options);

                                //Reset
                                if(this.otherTypes[i].sections[t].fields[j].type == 'Checkbox')
                                {
                                    options = [];
                                }

                                //Loop and Add
                                for(var v = 0; v < keys.length; v++)
                                {
                                    options.push({value: keys[v], text: this.otherTypes[i].sections[t].fields[j].options[keys[v]]});
                                }

                                //Send it
                                //return options;
                            }
                        }
                    }
                }

                //Send it
                return options;
            },

            updateField(section_id, field_id){
                //Setup
                // var sectionIdx = 0;
                // var fieldIdx = 0;
                // var field = null;

                // //Find the field
                // for (var i = this.sections.length - 1; i >= 0; i--)
                // {
                //     //Check the ID
                //     if(this.sections[i].id != section_id)
                //     {
                //         continue;
                //     }

                //     //Set ot
                //     sectionIdx = i;

                //     //Loop and build
                //     for (var j = this.sections[i].fields.length - 1; j >= 0; j--)
                //     {
                //         //Check the ID
                //         if(this.sections[i].fields[j].id != field_id)
                //         {
                //             continue;
                //         }

                //         //Set it
                //         fieldIdx = j;
                //         field = this.sections[i].fields[j];
                //     }
                // }

                // //Attempt to match the value if the type is a radio or dropdown
                // if(!field || field.type != 'Radio' && field.type != 'Dropdown')
                // {
                //     return;
                // }

                // //Setup
                // var matchedValue = field.value[0];
                // var fieldOptions = this.newFieldOptions(field.new_field_id);
                // var keys = Object.keys(fieldOptions);

                // console.log(fieldOptions);

                // //Try to match based on value
                // for (var i = keys.length - 1; i >= 0; i--)
                // {
                //     //console.log(fieldOptions[keys[i]]);

                //     //Check it
                //     if(matchedValue == fieldOptions[keys[i]])
                //     {
                //         field.new_value = keys[i];

                //         //console.log(fieldOptions[keys[i]]);
                //         //console.log(field);
                //     }
                // }

                // console.log(field);

                // //Update
                // this.sections[sectionIdx].fields[fieldIdx] = field;
                this.$forceUpdate();
            },

            testing(){

            },

            getFieldIndex(id){
                //Loop and check
                var idx = null;

                for (var i = this.fields.length - 1; i >= 0; i--)
                {
                    //Check it
                    if(this.fields[i].id == id)
                    {
                        idx = i;
                    }
                }

                //Send it
                return idx;
            }
        },

        mounted() {
            //Get the incidents
            this.getIncident();
        }
    }
</script>
